import { Component, computed, inject, Input, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { createForm } from '@sonofabit/ng-core';
import {
  BuildingScanItemDto,
  BuildingScanService,
  IdReferenceDto,
} from '@artemis-software/wr-api';
import { NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-building-scan-dialog',
  templateUrl: './building-scan-dialog.component.html',
})
export class BuildingScanDialogComponent implements OnInit {
  private readonly buildingScanService = inject(BuildingScanService);
  private readonly toastr = inject(NbToastrService);

  @Input() scanId!: string;
  @Input() buildingId!: string;
  @Input() name?: string;
  @Input() pointCloudId?: string;
  @Input() sectionIds?: string[];
  @Input() type: BuildingScanItemDto.TypeEnum = BuildingScanItemDto.TypeEnum.PointCloud;

  readonly header = computed(() => this.scanId ? 'Scan bearbeiten' : 'Neuen Scan hinzufügen');

  form?: ReturnType<typeof this.createForm>;

  ngOnInit(): void {
    this.form = this.createForm();
  }

  get isPointCloud(): boolean {
    return this.type === BuildingScanItemDto.TypeEnum.PointCloud;
  }

  private createForm() {
    return createForm(({ field }) => ({
      id: field({
        value: this.scanId,
      }),
      name: field({
        value: this.name,
        validators: [
          Validators.required,
        ],
      }),
      pointCloudId: field({
        value: this.pointCloudId,
        validators: this.isPointCloud
          ? [
            Validators.required,
            Validators.pattern(
              /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/
            ),
          ]
          : [],
      }),
      buildingId: field({
        value: this.buildingId,
      }),
      sections: field({
        value: (this.sectionIds ?? []).map((id): IdReferenceDto => ({ value: id })),
        validators: [Validators.required],
      }),
    }));
  }

  async onSubmit() {
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.toastr.warning(
        'Bitte gültige Scan-Daten eingeben.',
        'Ungültige Eingabe'
      );
      return false;
    }

    try {
      const { name, pointCloudId, buildingId, sections } = this.form.value;
      const sectionIds = (sections ?? []).map((section: IdReferenceDto) => section.value!);
      let buildingScan;
      if (!this.scanId) {
        buildingScan = await firstValueFrom(this.buildingScanService.createBuildingScan({ name: name!, pointCloudId: pointCloudId!, buildingId: buildingId!, sectionIds }));
      } else {
        buildingScan = await firstValueFrom(this.buildingScanService.editBuildingScan(this.scanId, {
          name: name!,
          pointCloudId: this.isPointCloud ? pointCloudId : undefined,
          sectionIds,
        }));
      }
      this.toastr.success(
        'Scan erfolgreich gespeichert.',
        'Gebäude aktualisiert'
      );
      return buildingScan;
    } catch {
      this.toastr.danger(
        'Scan konnten nicht gespeichert werden.',
        'Fehler'
      );
      return false;
    }
  }

  get disableSubmit(): boolean {
    return !this.form?.valid;
  }
}
